// @flow

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { selectUser, selectAccountSettings, selectAccountId } from '../../reduxStore/modules/user/selectors';
import { selectLoadingModules } from '../../reduxStore/modules/common/selectors';
import { setLoadingModule, setSnackMessage } from '../../reduxStore/modules/common/actions';
import { fetchUserProfile } from '../../reduxStore/modules/user/actions';
import useAuthenticatedBackend from '../../hooks/useAuthenticatedBackend';
import useOnboardingRedirect from '../../hooks/useOnboardingRedirect';
import { getNextOnboardingStep } from '../../utils/onboardingUtils';
import { log, formatPhoneNumberForBackend } from '../../utils/jsUtils';
import adTracking from '../../services/adTracking';
import SimpleMsgDialog from '../SharedComponents/SimpleMsgDialog';
import AppLoadingSpinner from '../SharedComponents/AppLoadingSpinner';
import ContentContainer from '../SharedComponents/ContentContainer';

import BasicHeader from '../Billing/BasicHeader.js';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Input from '@mui/material/Input';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

import { createClasses } from '../../utils/stylesUtils';
import twilioNumberHomeStyles from './Styles/TwilioNumberHome.Style';
import memberStructuralStyles from '../Styles/CommonMemberStructuralStyles.Style';
import sharedDialogStyles from '../Styles/SharedDialogStyles';

type Props = {};

const TwilioNumberHome = (props: Props) => {
  // Redux selectors
  const user = useSelector(selectUser);
  const accountSettings = useSelector(selectAccountSettings);
  const accountId = useSelector(selectAccountId);
  const loadingModules = useSelector(selectLoadingModules);

  // Hooks
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const backend = useAuthenticatedBackend();

  // Redirect if user shouldn't be on this page (e.g., already has phone number)
  useOnboardingRedirect();

  // Create classes from styles
  const classes = createClasses({
    ...twilioNumberHomeStyles,
    ...memberStructuralStyles,
    ...sharedDialogStyles
  }, theme);
  const [phoneNumbers, setPhoneNumbers] =  useState([]);
  const [openConfirmBuyNumber, setOpenConfirmBuyNumber] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [hover,setHover] = useState(null);
  const [numberSentToConfirm, setNumberSentToConfirm] = useState(false);
  const [textValue, setTextValue] =  useState('');
  const [countryValue, setCountryValue] =  useState('US');
  const isInitializedRef = useRef(false);

  const handleHoverToggle = (id) => { 
    setHover(id);
  };
  const handleHoverToggleOff = () => { 
    setHover(null);
  };

  const handleNumberSearch = (param: string, country: string) => {
    if (user !== null) {
      const searchPayload = {
        searchBy: param,
        country: country
      };
      log('phoneNumberSearch', searchPayload);
      backend.fetchPhoneNumbers(searchPayload)
      .then((response) => {
        log('phoneNumbers fetched', response.data);
        setPhoneNumbers(response.data);
        dispatch(setLoadingModule('phoneNumbers', false));
      })
      .catch(error => {
        log('[fetchPhoneNumbers] fetchPhoneNumbers error.response: ', error.response);
      });
    }
  };

  useEffect(() => {
    if (user !== null && !isInitializedRef.current) {
      isInitializedRef.current = true;
      dispatch(setLoadingModule('phoneNumbers', true));
      handleNumberSearch('', 'US');
    }
  }, [user]);

  const handlePickNumber = useCallback((phoneNumberObject: Object) => {
    log('pick phone number', phoneNumberObject);
    const number = phoneNumberObject.phoneNumber;
    const buyNumberPayload = {
      phoneNumber: formatPhoneNumberForBackend(number),
      accountId,
      twilioAccountSID: accountSettings?.Twilio?.SubaccountSID
    };
    setNumberSentToConfirm(true);

    // TODO: Handle default building phone setting - needs implementation in Redux
    // For now, this will be handled by the backend

    backend.buyPhoneNumber(buyNumberPayload)
    .then((response) => {
      log('[buyPhoneNumber] phoneNumber bought ', response.data);
      adTracking.trackCustomEvent('PhoneNumberAquired');
      // Fetch updated profile to get the new phone number in Redux
      dispatch(fetchUserProfile(user.uid))
        .then(() => {
          const nextStep = getNextOnboardingStep('/pickYourPhoneNumber');
          log('[buyPhoneNumber] redirecting to', nextStep);
          history.push(nextStep);
        })
        .catch((error) => {
          log('[buyPhoneNumber] Error fetching profile:', error);
          // Still redirect even if profile fetch fails - phone was purchased
          const nextStep = getNextOnboardingStep('/pickYourPhoneNumber');
          history.push(nextStep);
        });
    })
    .catch(error => {
      log('[buyPhoneNumber] buyPhoneNumber error.response: ', error.response);
      dispatch(setSnackMessage({
        message: 'Sorry this number has been taken! Please refresh this screen and try picking a new number',
        level: 'error',
        duration: 3000
      }));
      // Reset the confirmation state on error
      setNumberSentToConfirm(false);
    });
  }, [accountId, accountSettings, backend, dispatch, history, user]);

  // Memoize dialog content to prevent unnecessary re-renders that cause dialog flash
  const dialogActions = useMemo(() => (
    <Box>
      <Button
        sx={classes.actionButtons}
        onClick={(e) => {
          e.stopPropagation();
          setOpenConfirmBuyNumber(false);
        }}
        variant='outlined'
      >{'GO BACK'}</Button>
      <Button
        sx={classes.actionButtons}
        color="primary"
        disabled={numberSentToConfirm}
        onClick={(e) => {
          e.stopPropagation();
          handlePickNumber(phoneNumber);
        }}
        variant='contained'
      >
        {numberSentToConfirm ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'CONFIRM'
        )}
      </Button>
    </Box>
  ), [numberSentToConfirm, phoneNumber, classes.actionButtons, handlePickNumber]);

  const dialogContent = useMemo(() => (
    <Box>
      {'This will be your business number:'}
      <br />
      <br />
      <Box sx={{ fontSize: '2rem'}}>
        <b>{phoneNumber.friendlyName}</b>
      </Box>
    </Box>
  ), [phoneNumber.friendlyName]);

  return (
    <Box>
      <Helmet>
        <title>Neo | Pick your number</title>
      </Helmet>
      <BasicHeader
        title={'Pick Your Free Business Phone Number'}
        mobileTitle={'Pick Your Number'}
      />
      <AppLoadingSpinner />
      <ContentContainer hasSubheader={false}>
        {
        accountSettings && accountSettings.Twilio && accountSettings.Twilio.SubaccountSID !== '' && accountSettings.Twilio.SubaccountSID !== null
        ? 
          <Grid
              sx={classes.contentCtr}
              item
              xs={12}
          >
          <Box sx={classes.welcomeText}>
          <span>{'Last step!'}</span>
          <Typography variant='h3'>{'Pick your number'}</Typography>
          </Box>
          <Paper sx={classes.formPaper}
                  elevation={2}
                  square
          >
          <Box sx={classes.formContainer}>
            <Grid container direction="row" spacing={2} sx={classes.horizontalFormContainer}>
              <Grid item xs={12} sm={4}>
                <select
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc' }}
                  value={countryValue}
                  onChange={(e) => {
                    setCountryValue(e.target.value);
                    handleNumberSearch(textValue, e.target.value);
                  }}
                >
                  <option value='US'>United States</option>
                  <option value='CA'>Canada</option>
                </select>
              </Grid>
              <Grid item xs={12} sm={8}>
                <input
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc' }}
                  placeholder="Search by Area Code or State (e.g. '917', 'NY')"
                  aria-label="Search by Area Code or State"
                  value={textValue}
                  onChange={e => {
                    setTextValue(e.target.value);
                    handleNumberSearch(e.target.value, countryValue);
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box>
          <TableContainer sx={classes.table}>
            <Table aria-label="simple table"
                    
                    stickyHeader
            >
              <TableHead>
              {
                phoneNumbers && phoneNumbers.length > 0
                ?
                <TableRow>
                  <TableCell align='left'
                              sx={{...classes.headerCell, ...classes.phoneNumberCell}}
                  ><b>{'Number'}</b></TableCell>
                  <TableCell align='left'
                              sx={classes.headerCell}
                  ><b>{'Locality'}</b></TableCell>
                  <TableCell align='left'
                              sx={{...classes.headerCell, ...classes.buttonCell}}
                  />
                </TableRow>
                : null
              }
              </TableHead>
                <TableBody>
                { phoneNumbers && phoneNumbers.length > 0 && !loadingModules.phoneNumbers
                  ?
                    phoneNumbers.map((number, index) => (
                    <TableRow key={number.friendlyName}
                              onMouseEnter={()=> handleHoverToggle(index)}
                              onMouseLeave={handleHoverToggleOff}
                              style={hover === index ? {background: '#d3d3d3'}: {}}
                    >
                      <TableCell align='left'
                                  sx={classes.phoneNumberCell}
                      >{number.friendlyName}</TableCell>
                      <TableCell align='left'
                                  sx={classes.tableCell}
                      >{number.locality}{number.locality ? ', ' : ''}{number.region}</TableCell>
                      <TableCell align='right'
                                sx={classes.buttonCell}
                      >
                        <Button color="primary"
                                onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenConfirmBuyNumber(true);
                                        setPhoneNumber(number);
                                        log('[TwilioNumberContent] buying number ', number);
                                      }}
                                variant="contained"
                        > 
                        {'PICK'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                  : <TableRow>
                    <TableCell sx={classes.tableCtr}>
                    <Box>
                      <p>
                        {'No phone numbers found for your search.'}
                      </p>
                      
                      <Button color="primary"
                              href="#text-buttons"
                              onClick={() => { 
                                handleNumberSearch('', 'US');
                                setTextValue('');
                              }}
                      >
                        {'Clear search'}
                      </Button>

                    </Box>
                   </TableCell>
                  </TableRow>
                }

              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          <SimpleMsgDialog
                sx={classes.dialogBox}
                dialogActions={dialogActions}
                dialogContent={dialogContent}
                dialogTitle={'Are you sure?'}
                open={openConfirmBuyNumber}
          />
          </Paper>
          </Grid>
        : <Box sx={classes.contentCtr}>
            <p>
              {'Fetching account details...'}
            </p>
          </Box>
        }
      </ContentContainer>
    </Box>
  );
};

export default TwilioNumberHome;